import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Loader2, MapPin } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// "Datos del técnico" dialog of the Planning: photos and signatures received
// from the technician for one appointment (read-only). Open while appointmentId is set.
const TechDataDialog = ({ appointmentId, onClose }) => {
  const [techData, setTechData] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null); // cancel stale tech data fetches

  const fetchTechData = useCallback(async (id) => {
    // cancel any previous in-flight tech data request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/tech/appointments/${id}/data`,
        { signal: controller.signal }
      );
      if (!controller.signal.aborted) setTechData(res.data);
    } catch (err) {
      if (axios.isCancel(err) || err?.name === "CanceledError") return;
      setTechData(null);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (appointmentId) fetchTechData(appointmentId);
  }, [appointmentId, fetchTechData]);

  return (
    <Dialog open={!!appointmentId} onOpenChange={(open) => { if (!open) { onClose(); setTechData(null); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="tech-data-dialog">
        <DialogHeader>
          <DialogTitle>Datos del técnico</DialogTitle>
          <DialogDescription>Fotos y firmas recibidas</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : techData ? (
          <div className="space-y-4">
            {techData.captures && Object.keys(techData.captures).length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-slate-700 mb-2">Firmas y escritura</h3>
                <div className="grid grid-cols-3 gap-2">
                  {[["anexo_signature","Firma Anexo"],["anexo_nombre","Nombre Anexo"],["anexo_dni_handwritten","DNI Anexo"],
                    ["acuerdo_signature","Firma Acuerdo"],["acuerdo_nombre","Nombre Acuerdo"],["acuerdo_dni_handwritten","DNI Acuerdo"],
                    ["justificante_signature","Firma Justificante"],
                    ["plantilla_signature","Firma Plantilla CAE"]
                  ].map(([key, label]) => (
                    <div key={key} className="text-center">
                      <p className="text-[10px] text-slate-500 mb-1">{label}</p>
                      {techData.captures[key] ? (
                        <a href={techData.captures[key]} download={`${key}.png`} className="block border rounded-lg overflow-hidden hover:ring-2 ring-blue-400 transition-all">
                          <img src={techData.captures[key]} alt={label} className="w-full h-16 object-contain bg-white" />
                        </a>
                      ) : (
                        <div className="h-16 bg-slate-100 rounded-lg flex items-center justify-center text-[10px] text-slate-300">-</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {techData.photos && Object.keys(techData.photos).some(k => techData.photos[k]?.filter(p => p).length > 0) && (
              <div>
                <h3 className="text-sm font-bold text-slate-700 mb-2">Fotos</h3>
                {[["dni_recto","DNI Recto"],["dni_verso","DNI Verso"],["vivienda","Vivienda"],["calentador","Calentador"],["ventana","Ventana"],["avant","Antes"],["apres","Despues"],["extra","Extra"]].map(([cat, label]) => {
                  // Filter out null values (empty slots)
                  const catPhotos = (techData.photos[cat] || []).filter(url => url !== null && url !== undefined);
                  if (catPhotos.length === 0) return null;
                  return (
                    <div key={cat} className="mb-3">
                      <p className="text-xs font-semibold text-slate-600 mb-1">{label} ({catPhotos.length})</p>
                      <div className="grid grid-cols-5 gap-1.5">
                        {catPhotos.map((url, i) => {
                          // Find the original index for geo data
                          const originalIdx = techData.photos[cat].indexOf(url);
                          const geo = techData.geo?.[`${cat}_${originalIdx}`];
                          // Thumbnail URL for fast grid loading
                          const thumbUrl = `${BACKEND_URL}${url.replace('/api/tech/photo/', '/api/tech/photo-thumb/')}`;
                          // Full URL for click-to-view
                          const hasTimestamp = url.includes('_') && /\d{13}/.test(url);
                          const cacheBuster = hasTimestamp ? '' : `?cb=${Date.now()}`;
                          const fullUrl = `${BACKEND_URL}${url}${cacheBuster}`;
                          return (
                            <div key={i} className="relative">
                              <a href={fullUrl} target="_blank" rel="noreferrer"
                                className="aspect-square rounded overflow-hidden border hover:ring-2 ring-blue-400 transition-all block">
                                <img src={thumbUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                              </a>
                              {geo && (
                                <a href={`https://www.google.com/maps?q=${geo.lat},${geo.lng}`} target="_blank" rel="noreferrer"
                                  className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[7px] px-0.5 py-0.5 truncate hover:bg-black/90"
                                  title={geo.address || `${geo.lat.toFixed(5)}, ${geo.lng.toFixed(5)}`}
                                  data-testid={`geo-${cat}-${i}`}>
                                  <MapPin className="inline w-2 h-2 mr-0.5" />{geo.lat.toFixed(4)},{geo.lng.toFixed(4)}
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {(!techData.captures || Object.keys(techData.captures).length === 0) && (!techData.photos || !Object.keys(techData.photos).some(k => techData.photos[k]?.length > 0)) && (
              <p className="text-sm text-slate-400 text-center py-4">Sin datos recibidos</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-4">Sin datos</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TechDataDialog;
